import { useMemo, useState } from 'react'
import { dateText, translate, type Lang } from './i18n'
import type { AvailabilityEntry, OfficerProfile, RosterImport, Vehicle, VehicleHistory } from './api'
import { RosterImportPanel } from './RosterImport'
import { useShiftCodes } from './shifts'

const ESTADOS = ['Available', 'Absent', 'Restricted'] as const

/** Lo que se está por publicar: el recurso, su estado y, si no está, por qué. */
type Fila = { id: string; etiqueta: string; detalle: string; status: string; reason: string }

/** Sin acentos ni mayúsculas: nadie los escribe cuando busca a alguien entre quinientos. */
const plano = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

/**
 * El editor de disponibilidad, que es lo que comparten Flota y Personal.
 *
 * Publicar **reemplaza la lista completa del turno**: quien no venga en ella deja de estar
 * disponible. Por eso la pantalla muestra el catálogo entero con su estado, y no solo a los
 * publicados: si mostrara únicamente la lista actual, quitar a alguien sería imposible de
 * distinguir de no haberlo agregado nunca.
 */
export function AvailabilityList({
  lang, filas, onChange, onPublish, busy, publicados, editado, kind = 'officer',
}: {
  lang: Lang
  /** Un vehículo no está «ausente» ni en «servicio restringido»: se nombra como vehículo (H-07). */
  kind?: 'officer' | 'vehicle'
  filas: Fila[]
  onChange: (id: string, cambio: Partial<Fila>) => void
  /** Si se publicó: se dice junto al botón, y lo editado se conserva si no (H-74). */
  onPublish: () => Promise<boolean>
  busy: boolean
  publicados: number
  /** Sin cambios no hay qué publicar: volver a publicar lo mismo solo reescribe quién y cuándo. */
  editado: boolean
}) {
  const [publicado, setPublicado] = useState(false)
  const cambia = (id: string, cambio: Partial<Fila>) => { setPublicado(false); onChange(id, cambio) }
  const sinMotivo = filas.some((f) => f.status !== '' && f.status !== 'Available' && !f.reason.trim())

  // Buscar y filtrar (H-77, §16.3c): marcar a mano en una lista de 577 oficiales sin filtro es
  // donde se cuela el error. Varias palabras exigen que estén todas («central sargento»).
  const [busca, setBusca] = useState('')
  const [estado, setEstado] = useState<string>('todos')
  const palabras = plano(busca).split(/\s+/).filter(Boolean)
  const mostradas = filas.filter((f) =>
    (estado === 'todos' || f.status === estado) &&
    palabras.every((p) => plano(`${f.etiqueta} ${f.detalle}`).includes(p)))
  const cuenta = (s: string) => filas.filter((f) => f.status === s).length
  const etiqueta = (s: string) => translate(lang, kind === 'vehicle' ? `availability.vehicle.${s}` : `availability.${s}`)
  // Arriba y abajo: la lista puede tener cientos de filas, y el motivo que falta o el botón no
  // pueden quedar donde nadie los ve (hallazgo H-08).
  const publicar = (
    <div className="row-line" style={{ flexWrap: 'wrap', gap: 8 }}>
      <button className="primary" disabled={busy || sinMotivo || !editado} onClick={() => void onPublish().then(setPublicado)}>
        {translate(lang, 'resources.publish')}
      </button>
      {publicado && <span className="chip ok" role="status">{translate(lang, 'resources.published', [publicados])}</span>}
      {!publicado && !editado && <span className="meta">{translate(lang, 'resources.nothingToPublish')}</span>}
      {sinMotivo && <span className="meta">{translate(lang, kind === 'vehicle' ? 'resources.needsReasonVehicle' : 'resources.needsReason')}</span>}
    </div>
  )

  return (
    <>
      <p className="notice">{translate(lang, 'resources.replaces', [publicados])}</p>
      {publicar}
      <div className="card">
        <div className="row-line" style={{ flexWrap: 'wrap', gap: 8 }}>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={translate(lang, kind === 'vehicle' ? 'resources.searchVehicle' : 'resources.search')}
            aria-label={translate(lang, kind === 'vehicle' ? 'resources.searchVehicle' : 'resources.search')}
            style={{ flex: '1 1 220px' }}
          />
          <select value={estado} onChange={(e) => setEstado(e.target.value)} aria-label={translate(lang, 'resources.filterStatus')}>
            <option value="todos">{translate(lang, 'resources.all', [filas.length])}</option>
            <option value="">{translate(lang, 'resources.notInShift')} · {cuenta('')}</option>
            {ESTADOS.map((s) => <option key={s} value={s}>{etiqueta(s)} · {cuenta(s)}</option>)}
          </select>
        </div>
        <div className="row-line" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className="meta grow">{translate(lang, 'resources.showing', [mostradas.length, filas.length])}</span>
          {/* De a muchos solo lo que no pide motivo: ausente o restringido se dice de uno en uno. */}
          <button disabled={busy || mostradas.length === 0}
            onClick={() => mostradas.forEach((f) => cambia(f.id, { status: 'Available', reason: '' }))}>
            {translate(lang, 'resources.markShownAvailable', [mostradas.length])}
          </button>
          <button disabled={busy || mostradas.length === 0}
            onClick={() => mostradas.forEach((f) => cambia(f.id, { status: '', reason: '' }))}>
            {translate(lang, 'resources.markShownOut', [mostradas.length])}
          </button>
        </div>
        {mostradas.length === 0 && <p className="notice">{translate(lang, 'resources.noneMatch')}</p>}
        {mostradas.map((f) => (
          <div key={f.id} className="row-line resource">
            <span className="grow">
              <strong>{f.etiqueta}</strong> <span className="meta">{f.detalle}</span>
            </span>
            <select
              value={f.status}
              onChange={(e) => cambia(f.id, { status: e.target.value })}
              aria-label={translate(lang, 'resources.status')}
            >
              <option value="">{translate(lang, 'resources.notInShift')}</option>
              {ESTADOS.map((s) => (
                <option key={s} value={s}>{etiqueta(s)}</option>
              ))}
            </select>
            {f.status !== '' && f.status !== 'Available' && (
              <input
                value={f.reason}
                onChange={(e) => cambia(f.id, { reason: e.target.value })}
                placeholder={translate(lang, 'resources.reason')}
                style={{ width: 180 }}
              />
            )}
          </div>
        ))}
        {publicar}
      </div>
    </>
  )
}

function ShiftPicker({ lang, date, code, onShift }: {
  lang: Lang; date: string; code: string; onShift: (d: string, c: string) => void
}) {
  const shifts = useShiftCodes()
  return (
    <section className="panel">
      <h2>{translate(lang, 'planner.shift')}</h2>
      <div className="card">
        <div className="actions">
          <input type="date" value={date} onChange={(e) => onShift(e.target.value, code)}
            aria-label={translate(lang, 'planner.shift')} />
          <select value={code} onChange={(e) => onShift(date, e.target.value)}
            aria-label={translate(lang, 'planner.shift')}>
            {shifts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </section>
  )
}

/** Usa las filas del catálogo y lo publicado para armar el estado editable. */
function useFilas(
  catalogo: { id: string; etiqueta: string; detalle: string }[],
  publicada: AvailabilityEntry[],
) {
  const inicial = useMemo(() => {
    const porId = new Map(publicada.map((a) => [a.resourceId, a]))
    return catalogo.map((c) => ({
      ...c,
      status: porId.get(c.id)?.status ?? '',
      reason: porId.get(c.id)?.reason ?? '',
    }))
  }, [catalogo, publicada])

  const [editadas, setEditadas] = useState<Record<string, Partial<Fila>>>({})
  const filas = inicial.map((f) => ({ ...f, ...editadas[f.id] }))
  const cambiar = (id: string, cambio: Partial<Fila>) =>
    setEditadas((previas) => ({ ...previas, [id]: { ...previas[id], ...cambio } }))
  const limpiar = () => setEditadas({})
  return { filas, cambiar, limpiar, editado: Object.keys(editadas).length > 0 }
}

/**
 * Dónde ha andado un vehículo (§9.2).
 *
 * El resumen primero y los turnos después, porque las dos preguntas son distintas: «¿este
 * vehículo trabaja?» se contesta con tres números, y «¿quién lo traía el martes?» con la lista.
 *
 * **Asignadas y completadas van juntas** a propósito. Un vehículo que se conformó todos los días
 * y no completó nada estuvo asignado en el papel y no en la calle, y ese es exactamente el caso
 * que el Fleet Manager no puede ver de otra manera.
 */
function VehicleHistoryPanel({ lang, history }: { lang: Lang; history: VehicleHistory }) {
  const cuando = (iso: string) => dateText(lang, iso)

  return (
    <section className="panel">
      <h2>{translate(lang, 'fleet.historyTitle', [history.vehicle.unitNumber])}</h2>

      {history.shiftsWorked === 0 ? (
        <p className="notice">{translate(lang, 'fleet.neverOut')}</p>
      ) : (
        <>
          <div className="card">
            <div className="meta">
              {translate(lang, 'fleet.historySummary', [
                history.shiftsWorked, history.totalCompleted, history.totalAssignments,
              ])}
            </div>
            <div className="meta">
              {translate(lang, 'fleet.historyRange', [
                cuando(history.firstShift!), cuando(history.lastShift!),
              ])}
            </div>
          </div>

          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'fleet.shift')}</th>
                <th>{translate(lang, 'fleet.unit')}</th>
                <th>{translate(lang, 'fleet.crew')}</th>
                <th className="num">{translate(lang, 'fleet.work')}</th>
              </tr>
            </thead>
            <tbody>
              {history.shifts.map((t) => (
                <tr key={`${t.shiftDate}-${t.shiftCode}-${t.callSign}`}>
                  <td>{t.shiftDate} · {t.shiftCode}</td>
                  <td>{t.callSign} <span className="meta">{t.division}</span></td>
                  <td className="meta">{t.crew.join(', ')}</td>
                  <td className="num">{t.completed}/{t.assignments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}

export function FleetScreen({
  lang, date, code, onShift, vehicles, availability, busy, onStatus, onPublish,
  history, onHistory, onImport, onImported,
}: {
  lang: Lang
  date: string
  code: string
  onShift: (d: string, c: string) => void
  vehicles: Vehicle[]
  availability: AvailabilityEntry[]
  busy: boolean
  onStatus: (vehicleId: string, status: string) => void
  onPublish: (entries: AvailabilityEntry[]) => Promise<boolean>
  history: VehicleHistory | null
  onHistory: (vehicleId: string | null) => void
  onImport: (file: File) => Promise<RosterImport>
  onImported: () => void
}) {
  const catalogo = useMemo(
    () => vehicles.map((v) => ({
      id: v.vehicleId,
      etiqueta: v.unitNumber,
      // El estado del vehículo va a la vista al publicar: sin él, uno en taller se marcaba
      // «disponible» para el turno sin que nada lo advirtiera (PD-07). Y entra en la búsqueda.
      detalle: `${v.vehicleType} · ${v.baseDivision}` +
        (v.status === 'Available' ? '' : ` · ${translate(lang, `vehicle.${v.status}`)}`),
    })), [vehicles, lang])
  const { filas, cambiar, limpiar, editado } = useFilas(catalogo, availability)

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'fleet.help')}</p>
        <RosterImportPanel lang={lang} kind="vehicles" onImport={onImport} onDone={onImported} />
        <ShiftPicker lang={lang} date={date} code={code} onShift={onShift} />

        <section className="panel">
          <h2>{translate(lang, 'fleet.catalogue', [vehicles.length])}</h2>
          <p className="notice">{translate(lang, 'fleet.statusHelp')}</p>
          <div className="card">
            {vehicles.map((v) => (
              <div key={v.vehicleId} className="row-line resource">
                <span className="grow">
                  <strong>{v.unitNumber}</strong>{' '}
                  <span className="meta">{v.vehicleType} · {v.baseDivision}</span>
                </span>
                <select
                  value={v.status}
                  disabled={busy}
                  onChange={(e) => onStatus(v.vehicleId, e.target.value)}
                  aria-label={translate(lang, 'fleet.status')}
                >
                  {['Available', 'Assigned', 'Maintenance', 'OutOfService'].map((s) => (
                    <option key={s} value={s}>{translate(lang, `vehicle.${s}`)}</option>
                  ))}
                </select>
                {/*
                  El catálogo se administra por vehículo y la operación lo usa por unidad. Este
                  botón cierra esa distancia: «¿quién traía la V-12 el martes?» se contestaba
                  abriendo plan por plan.
                */}
                <button
                  disabled={busy}
                  onClick={() => onHistory(history?.vehicle.vehicleId === v.vehicleId ? null : v.vehicleId)}
                >
                  {translate(lang, history?.vehicle.vehicleId === v.vehicleId ? 'fleet.closeHistory' : 'fleet.history')}
                </button>
              </div>
            ))}
          </div>
        </section>

        {history && <VehicleHistoryPanel lang={lang} history={history} />}

        <section className="panel">
          <h2>{translate(lang, 'resources.availability')}</h2>
          <AvailabilityList
            lang={lang}
            kind="vehicle"
            filas={filas}
            onChange={cambiar}
            busy={busy}
            publicados={filas.filter((f) => f.status !== '').length}
            editado={editado}
            onPublish={() => onPublish(filas.filter((f) => f.status !== '')
              .map((f) => ({ resourceId: f.id, status: f.status, reason: f.reason })))
              // Si el servidor lo rechazó, lo editado se queda: antes se perdía igual.
              .then((ok) => { if (ok) limpiar(); return ok })}
          />
        </section>
      </aside>
    </div>
  )
}

export function HrScreen({
  lang, date, code, onShift, officers, availability, busy, onActive, onPublish,
  onImport, onImported,
}: {
  lang: Lang
  date: string
  code: string
  onShift: (d: string, c: string) => void
  officers: OfficerProfile[]
  availability: AvailabilityEntry[]
  busy: boolean
  onActive: (officerId: string, isActive: boolean) => void
  onPublish: (entries: AvailabilityEntry[]) => Promise<boolean>
  onImport: (file: File) => Promise<RosterImport>
  onImported: () => void
}) {
  const activos = useMemo(() => officers.filter((o) => o.isActive), [officers])
  const catalogo = useMemo(
    () => activos.map((o) => ({
      id: o.officerId,
      etiqueta: o.badge,
      detalle: `${o.rank} · ${o.homeDivision} · ${translate(lang, 'crew.years', [o.years])}`,
    })), [activos, lang])
  const { filas, cambiar, limpiar, editado } = useFilas(catalogo, availability)

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'hr.help')}</p>
        <RosterImportPanel lang={lang} kind="officers" onImport={onImport} onDone={onImported} />
        <ShiftPicker lang={lang} date={date} code={code} onShift={onShift} />

        <section className="panel">
          <h2>{translate(lang, 'resources.availability')}</h2>
          <AvailabilityList
            lang={lang}
            kind="officer"
            filas={filas}
            onChange={cambiar}
            busy={busy}
            publicados={filas.filter((f) => f.status !== '').length}
            editado={editado}
            onPublish={() => onPublish(filas.filter((f) => f.status !== '')
              .map((f) => ({ resourceId: f.id, status: f.status, reason: f.reason })))
              // Si el servidor lo rechazó, lo editado se queda: antes se perdía igual.
              .then((ok) => { if (ok) limpiar(); return ok })}
          />
        </section>

        <section className="panel">
          <h2>{translate(lang, 'hr.roster', [officers.length])}</h2>
          <p className="notice">{translate(lang, 'hr.rosterHelp')}</p>
          <div className="card">
            {officers.map((o) => (
              <div key={o.officerId} className={`row-line resource ${o.isActive ? '' : 'taken'}`}>
                <span className="grow">
                  <strong>{o.badge}</strong>{' '}
                  <span className="meta">
                    {o.rank} · {o.homeDivision} · {translate(lang, 'crew.years', [o.years])}
                    {o.certifications.length > 0 && ` · ${o.certifications.join(', ')}`}
                  </span>
                </span>
                <label className="meta">
                  <input
                    type="checkbox"
                    checked={o.isActive}
                    disabled={busy}
                    onChange={(e) => onActive(o.officerId, e.target.checked)}
                  />
                  {' '}{translate(lang, 'hr.active')}
                </label>
              </div>
            ))}
          </div>
        </section>
      </aside>
    </div>
  )
}
